import React, {
  type ComponentProps,
  type ComponentPropsWithoutRef,
  type ElementType,
  forwardRef,
  type ReactElement,
  Ref,
} from 'react';
import { Stack as MuiStack } from '@mui/material';

type HTMLTagMap = {
  div: HTMLDivElement;
  span: HTMLSpanElement;
  a: HTMLAnchorElement;
  button: HTMLButtonElement;
};

type MuiProps = ComponentProps<typeof MuiStack>;

type PolymorphicRef<C extends ElementType> = C extends keyof HTMLTagMap ? Ref<HTMLTagMap[C]> : Ref<unknown>;

export type Props<C extends ElementType> = {
  component?: C;
  fullWidth?: boolean;
} & MuiProps &
  Omit<ComponentPropsWithoutRef<C>, keyof MuiProps | 'component'>;

function PolymorphicComponentImpl<C extends ElementType = 'div'>(
  props: Props<C>,
  ref: PolymorphicRef<C>,
): ReactElement {
  const { fullWidth, ...rest } = props;
  return (
    <MuiStack
      {...rest}
      data-full-width={props['data-full-width'] || fullWidth}
      // TypeScript can't infer polymorphic ref on MUI base, safe to cast
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref={ref as Ref<any>}
    />
  );
}

const PolymorphicComponent = forwardRef(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  PolymorphicComponentImpl as unknown as (props: Props<any>, ref: PolymorphicRef<any>) => ReactElement,
);

const Stack = PolymorphicComponent as <C extends ElementType = 'div'>(
  props: Props<C> & { ref?: PolymorphicRef<C> },
) => ReactElement | null;

// @ts-expect-error TS can't detect displayName from forwardRef
Stack.displayName = 'Stack';

export default Stack;
